import { Injectable, inject } from "@angular/core";
import {
  SfLocalizeService,
  SfMessageService,
  SfError,
  SfErrorEnum,
} from "@app/sf-lib/public_api";
import { Observable, of, throwError, forkJoin } from "rxjs";

import { contact } from "@assets/mock/contact.mock";
import { SomContactPage } from "@app/core/models/contact-page.model";
import { SomForm } from "@app/core/models/form.model";
import { map, catchError } from "rxjs/operators";
import { NetworkService } from "@app/core/networking/services/network.service";
import { RequestContactInfo } from "@app/core/request/models/request-contact-info.model";
import { HttpClient } from "@angular/common/http";

export interface FormOption {
  label: string;
  value: string;
}

@Injectable({ providedIn: "root" })
export class ContactService {
  private readonly localize = inject(SfLocalizeService);
  private readonly messageService = inject(SfMessageService);
  private readonly http = inject(HttpClient);
  private readonly somNetwork = inject(NetworkService);

  protected readonly UPLOAD = "/upload";
  private readonly SEND_MAIL = "/send/contact/mail";

  public getContactPage(): Observable<SomContactPage> {
    const localContact = contact.find((f) => {
      return (
        f.lang.country === this.localize.currentLang.country &&
        f.lang.code === this.localize.currentLang.code
      );
    });
    return of(localContact);
  }

  public findForm(url: string): Observable<SomForm> {
    const cleanUrl = url.replace(/\//g, "");
    return this.getContactPage().pipe(
      map((page) => {
        return this.findFormInContactPage(cleanUrl, page);
      }),
    );
  }

  private findFormInContactPage(url: string, c: SomContactPage): SomForm {
    const form = c.forms.find((f: SomForm) => {
      const segment = f.link?.url.split("/").pop();
      return segment === url;
    });
    return form;
  }

  // Request

  /**
   * Posts a form request
   * @returns the id of the request created
   * @throws {SfError}
   */
  public sendForm(
    contactInfo: RequestContactInfo,
    requestInfo: Object,
    token: string,
  ): Observable<any> {
    requestInfo = this.processRequestInfo(requestInfo);

    return this.http
      .post(`${this.somNetwork.apiUrl}/contact-request`, {
        contact: contactInfo,
        request: requestInfo,
        token,
      })
      .pipe(
        catchError((httpError) => {
          const error: SfError =
            this.messageService.getErrorForLocaleCode(
              SfErrorEnum.internal_error,
            );

          error.additionalInfo =
            httpError?.error?.message ??
            "Contact request failed";

          return throwError(() => error);
        }),
      );
  }
  /**
   * Add nested information befor sending them to request
   */
private processRequestInfo(
  requestInfo: Object,
): Object {
  requestInfo["Channel"] = this.getChannel();
  requestInfo["CS_PurchaseCountry"] = this.getCountryCode();

  return requestInfo;
}

  private getChannel(): String {
    return `API_BEL_Produit_Information`;
  }

  public uploadPhotos(photos: Blob[]): Observable<string[]> {
    const posts: Observable<string>[] = [];
    if (!photos || photos.length < 1) {
      return of([]);
    }

    for (let i = 0; i < photos.length; i++) {
      const formData = new FormData();
      formData.append(
        `myfile${i}`,
        photos[i],
        `myfile${i}.${photos[i].type.split("/")[1]}`,
      );
      posts.push(
        this.http.post(this.somNetwork.apiUrl + this.UPLOAD, formData).pipe(
          catchError(() => of("")),
          map((response) => {
            if (response && typeof response === "string") {
              return response;
            } else {
              return "";
            }
          }),
        ),
      );
    }

    return forkJoin(posts);
  }

  public sendMail(body: {}) {
    this.http
      .post(this.somNetwork.apiUrl + this.SEND_MAIL, body)
      .subscribe((res) => {});
  }

  private getCountryCode(): string {
    let code = "2";
    switch (this.localize.currentLang.country) {
      case "ba":
        {
          code = "52";
        }
        break;
      case "bg":
        {
          code = "54";
        }
        break;
      case "fr":
        {
          code = "2";
        }
        break;
      case "nl":
        {
          code = "12";
        }
        break;
      case "be":
        {
          this.localize.currentLang.code === "fr"
            ? (code = "3")
            : (code = `12`);
        }
        break;
      case "de":
        {
          code = "8";
        }
        break;
      case "at":
        {
          code = "47";
        }
        break;
      case "it":
        {
          code = "11";
        }
        break;
      case "pt":
        {
          code = "15";
        }
        break;
      case "es":
        {
          code = "19";
        }
        break;
      case "sk":
        {
          code = "58";
        }
        break;
      case "cz":
        {
          code = "58";
        }
        break;
      case "hu":
        {
          code = "64";
        }
        break;
      case "hr":
        {
          code = "10";
        }
        break;
      case "dk":
        {
          code = "491";
        }
        break;
      case "lt":
        {
          code = "97";
        }
        break;
      case "ee":
        {
          code = "60";
        }
        break;
      case "lv":
        {
          code = "96";
        }
        break;
      case "se":
        {
          code = "86";
        }
        break;
      case "no":
        {
          code = "9";
        }
        break;
      case "fi":
        {
          code = "16";
        }
        break;
      case "pl":
        {
          code = "14";
        }
        break;
      case "al":
        {
          code = "324";
        }
        break;
      case "rs":
        {
          code = "347";
        }
        break;
      case "gr":
        {
          code = "62";
        }
        break;
      case "ro":
        {
          code = "13";
        }
        break;
      case "br":
        {
          code = "49";
        }
        break;
      default:
        {
          code = `2`;
        }
        break;
    }
    return code;
  }
}
